/**
 * Page-level contextual help identifiers.
 *
 * Keys are Angular route configuration paths with route parameters removed, joined from the root
 * of the activated route tree. `/employee/EmployeeViewEdit/12/asc` is declared as
 * `EmployeeViewEdit/:id/:sortdirec` under `employee`, which reduces to `employee/EmployeeViewEdit`
 * — so one entry covers every parameter combination of a page, and list/detail variants that
 * differ only by an optional id do not need duplicate entries.
 *
 * Values follow the help identifier contract: `module.feature.context`, at least three
 * dot-separated segments, each starting with a lowercase letter (camelCase allowed). Identifiers
 * are language-neutral — never suffix them with a locale.
 *
 * Lookup is case-insensitive. A page with no entry shows no help affordance at all, which is why
 * these are exhaustive rather than best-effort.
 */
export const PAGE_HELP_KEYS: { [routePath: string]: string } = {
  // --- Payroll -------------------------------------------------------------------------------
  'dashboard': 'payroll.dashboard.overview',
  'yearend/YearEnd': 'payroll.yearEnd.overview',

  // --- Payroll > Employees -------------------------------------------------------------------
  'employee/EmployeeList': 'payroll.employee.list',
  'employee/EmployeeViewEdit': 'payroll.employee.details',
  'employee/PayrollCategoryEntitlementEmployeeWise': 'payroll.employee.categoryEntitlement',
  'employee/PaycardHistory': 'payroll.employee.paycardHistory',
  'employee/PaystubHistory': 'payroll.employee.paystubHistory',
  'employee/ChequeReconciliation': 'payroll.employee.chequeReconciliation',

  // --- Payroll > Payroll setup ---------------------------------------------------------------
  'payrollsetup/PayrollGroupList': 'payroll.payrollSetup.payrollGroupList',
  'payrollsetup/PayrollGroupDetail': 'payroll.payrollSetup.payrollGroupDetail',
  'payrollsetup/PayrollGroupSetting': 'payroll.payrollSetup.payrollGroupSetting',
  'payrollsetup/PayrollCategory': 'payroll.payrollSetup.payrollCategoryList',
  'payrollsetup/PayrollCategoryClassification': 'payroll.payrollSetup.payrollCategoryClassification',
  'payrollsetup/PayrollCategoryDetail': 'payroll.payrollSetup.payrollCategoryDetail',
  'payrollsetup/PayrollCategoryEntitlement': 'payroll.payrollSetup.payrollCategoryEntitlement',
  'payrollsetup/PayrollCategoryTaxFormBoxes': 'payroll.payrollSetup.payrollCategoryTaxFormBoxes',
  'payrollsetup/JobCostStructure': 'payroll.payrollSetup.jobCostStructure',
  'payrollsetup/JobCostStructureEdit': 'payroll.payrollSetup.jobCostStructureEdit',

  // --- Payroll > Payroll setup > GL ----------------------------------------------------------
  'payrollsetup/gl/GlAccountSetup': 'payroll.glSetup.accountSetup',
  'payrollsetup/gl/GlStructure': 'payroll.glSetup.structure',
  'payrollsetup/gl/GLStructureEdit': 'payroll.glSetup.structureEdit',
  'payrollsetup/gl/GLEmployeeStructureEdit': 'payroll.glSetup.employeeStructureEdit',

  // --- Payroll > Payroll setup > Labor -------------------------------------------------------
  'payrollsetup/labor/LaborSetup': 'payroll.laborSetup.overview',
  'payrollsetup/labor/Dimension': 'payroll.laborSetup.dimension',
  'payrollsetup/labor/DimentionDetails': 'payroll.laborSetup.dimensionDetails',
  'payrollsetup/labor/UnionAgreement': 'payroll.laborSetup.unionAgreement',
  'payrollsetup/labor/CertifiedPayroll': 'payroll.laborSetup.certifiedPayroll',
  'payrollsetup/labor/CategoryCondition': 'payroll.laborSetup.categoryCondition',

  // --- Payroll > Processing ------------------------------------------------------------------
  'process/processwizard/payPeriod': 'payroll.processing.payPeriod',
  'process/processwizard/Labor': 'payroll.processing.labor',
  'process/processwizard/PayrollProcess': 'payroll.processing.payrollProcess',
  'process/processwizard/TimeSheet': 'payroll.processing.timeSheet',
  'process/processwizard/PrintChequeAndPaystub': 'payroll.processing.printChequeAndPaystub',
  'process/processwizard/PayrollCategoryEntitlementEmployeeWise': 'payroll.processing.categoryEntitlement',
  'process/processwizard/VisaFinalize': 'payroll.processing.visaFinalize',
  'process/processwizard/DataExportList': 'payroll.processing.dataExport',
  'process/processwizard/BankFile': 'payroll.processing.bankFile',
  'process/processwizard/Reports': 'payroll.processing.reports',
  'process/processwizard/BackUp': 'payroll.processing.backup',
  'process/processwizard/wizard': 'payroll.processing.wizard',
  'process/processwizard/Wizard': 'payroll.processing.wizard',
  'process/processgl/CreateGlRecords': 'payroll.processGl.createRecords',
  'process/processgl/GLCreateExport': 'payroll.processGl.createExport',
  'process/processjobcost/CreateJobCostRecords': 'payroll.processJobCost.createRecords',
  'process/processjobcost/JobCostCreateExport': 'payroll.processJobCost.createExport',
  'process/certifiedpayroll/CertifiedPayrollExport': 'payroll.certifiedPayroll.export',

  // --- Payroll > Reports --------------------------------------------------------------------
  // `report/reports` is a redirect stub: PayrollReportComponent renders nothing and immediately
  // navigates to ReportNavigator/Payroll. Both therefore describe the same visible page and share
  // one article -- mapping them apart left the page users actually see pointing at a key nobody
  // had authored.
  'report/reports': 'payroll.reports.list',
  // ReportNavigator serves a different page per :module, so the concrete value distinguishes them.
  'report/ReportNavigator/Payroll': 'payroll.reports.list',
  'report/ReportNavigator/Configuration': 'configuration.employee.reports',
  'report/ReportNavigator': 'payroll.reports.list',
  'report/ReportImport': 'payroll.reports.import',
  'report/dynamicReports': 'payroll.reports.dynamicList',
  'report/dynamicReport': 'payroll.reports.dynamicViewer',

  // --- Payroll > Tax forms > Canada ---------------------------------------------------------
  'taxforms/taxformcanada/taxformcanadaT4/T4Forms': 'payroll.taxForms.t4List',
  'taxforms/taxformcanada/taxformcanadaT4/T4edit': 'payroll.taxForms.t4Edit',
  'taxforms/taxformcanada/taxformcanadaT4/T4SummaryEdit': 'payroll.taxForms.t4Summary',
  'taxforms/taxformcanada/taxformcanadaT4/T4XML': 'payroll.taxForms.t4Xml',
  'taxforms/taxformcanada/taxformcanadaT4a/T4AForms': 'payroll.taxForms.t4aList',
  'taxforms/taxformcanada/taxformcanadaT4a/T4Aedit': 'payroll.taxForms.t4aEdit',
  'taxforms/taxformcanada/taxformcanadaT4a/T4ASummaryEdit': 'payroll.taxForms.t4aSummary',
  'taxforms/taxformcanada/taxformcanadaT4a/T4AXML': 'payroll.taxForms.t4aXml',
  'taxforms/taxformcanada/taxformcanadaT4a/T4APrint': 'payroll.taxForms.t4aPrint',
  'taxforms/taxformcanada/taxformcanadaRl1/RL-1': 'payroll.taxForms.rl1List',
  'taxforms/taxformcanada/taxformcanadaRl1/RL1edit': 'payroll.taxForms.rl1Edit',
  'taxforms/taxformcanada/taxformcanadaRl1/RL1SummaryEdit': 'payroll.taxForms.rl1Summary',
  'taxforms/taxformcanada/taxformcanadaRl1/RL1XML': 'payroll.taxForms.rl1Xml',
  'taxforms/taxformcanada/taxformcanadaRl1/RL1Print': 'payroll.taxForms.rl1Print',
  'taxforms/taxformcanada/taxformcanadaRoe/Roe': 'payroll.taxForms.roeList',
  'taxforms/taxformcanada/taxformcanadaRoe/RoeWeb': 'payroll.taxForms.roeWeb',
  'taxforms/taxformcanada/taxformcanadaRoe/RoeDetail': 'payroll.taxForms.roeDetail',

  // --- Payroll > Tax forms > US -------------------------------------------------------------
  'taxforms/taxformus/TaxForms': 'payroll.taxForms.usForms',
  'taxforms/taxformus/TaxFormsExport': 'payroll.taxForms.usExport',

  // --- Payroll > Admin settings -------------------------------------------------------------
  'adminsetup/adminsetupsetup/SystemPref': 'payroll.adminSetup.systemPreferences',
  'adminsetup/adminsetupsetup/BankList': 'payroll.adminSetup.bankList',
  'adminsetup/adminsetupsetup/UDFList': 'payroll.adminSetup.userDefinedFields',
  'adminsetup/adminsetupsetup/UDFGroupDetail': 'payroll.adminSetup.userDefinedFieldGroup',
  'adminsetup/adminsetupsetup/PaystubImport': 'payroll.adminSetup.paystubImport',
  'adminsetup/adminsetupsetup/InternalImportUtility': 'payroll.adminSetup.internalImportUtility',
  'adminsetup/interface/DataExportList': 'payroll.adminSetup.dataExportInterface',
  'adminsetup/interface/BankFile': 'payroll.adminSetup.bankFileInterface',
  'adminsetup/interface/GLList': 'payroll.adminSetup.glInterfaceList',
  'adminsetup/interface/GLEdit': 'payroll.adminSetup.glInterfaceEdit',
  'adminsetup/interface/JobCostList': 'payroll.adminSetup.jobCostInterfaceList',
  'adminsetup/interface/JobCostEdit': 'payroll.adminSetup.jobCostInterfaceEdit',
  'adminsetup/interface/PaycardList': 'payroll.adminSetup.paycardInterfaceList',
  'adminsetup/interface/PaycardListDetailComponent': 'payroll.adminSetup.paycardInterfaceDetail',

  // --- Configuration > Setup ----------------------------------------------------------------
  'configuration/configurationsetup/Company': 'configuration.setup.company',
  'configuration/configurationsetup/Departments': 'configuration.setup.departments',
  'configuration/configurationsetup/Locations': 'configuration.setup.locations',
  'configuration/configurationsetup/Calendars': 'configuration.setup.calendars',
  'configuration/configurationsetup/Email': 'configuration.setup.email',
  'configuration/configurationsetup/SysPref': 'configuration.setup.systemPreferences',

  // --- Configuration > Employees ------------------------------------------------------------
  'configuration/configurationemployee/CoreEmployee': 'configuration.employee.list',
  'configuration/configurationemployee/CoreEmployeedetail': 'configuration.employee.details',
  'configuration/configurationemployee/employee-reports': 'configuration.employee.reports',
  'configuration/configurationemployee/GLIntegration': 'configuration.employee.glIntegration',

  // --- Configuration > Tools ----------------------------------------------------------------
  'configuration/tools/Backup': 'configuration.tools.backup',
  'configuration/tools/Restore': 'configuration.tools.restore',
  'configuration/tools/email-log': 'configuration.tools.emailLog',
  'configuration/tools/employee-count-analysis': 'configuration.tools.employeeCountAnalysis',

  // --- User setup ----------------------------------------------------------------------------
  'usersetup/UserSettings': 'userSetup.settings.overview'
};

/** Case-insensitive index, built once. */
const NORMALIZED_KEYS: { [lowerRoutePath: string]: string } = Object.keys(PAGE_HELP_KEYS)
  .reduce((acc, key) => {
    acc[key.toLowerCase()] = PAGE_HELP_KEYS[key];
    return acc;
  }, {} as { [lowerRoutePath: string]: string });

/**
 * Route segments that wrap a page without changing which page it is.
 *
 * The payroll wizard declares its steps twice: once as children of a `wizard` shell route
 * (wizard mode) and once standalone. Both render the same components, so
 * `process/processwizard/wizard/payPeriod` is the same page as `process/processwizard/payPeriod`
 * and shares its help article rather than needing a duplicate entry per step.
 */
const SHELL_SEGMENTS = ['wizard'];

/**
 * Reduces a route configuration path to its parameter-free form:
 * `employee/EmployeeViewEdit/:id/:sortdirec` -> `employee/EmployeeViewEdit`.
 */
export function normalizeRoutePath(routePath: string): string {
  return (routePath || '')
    .split('/')
    .filter(segment => segment.length > 0 && segment.charAt(0) !== ':')
    .join('/');
}

/** Drops shell segments so a wrapped page resolves to the page it actually is. */
function withoutShellSegments(routePath: string): string {
  return routePath
    .split('/')
    .filter(segment => SHELL_SEGMENTS.indexOf(segment.toLowerCase()) === -1)
    .join('/');
}

/** Returns the help identifier for a route configuration path, or null when the page has none. */
export function helpKeyForRoutePath(routePath: string): string | null {
  const normalized = normalizeRoutePath(routePath);
  if (!normalized) {
    return null;
  }

  const exact = NORMALIZED_KEYS[normalized.toLowerCase()];
  if (exact) {
    return exact;
  }

  // A page reached through a shell route (the payroll wizard) resolves to the page itself.
  const unwrapped = withoutShellSegments(normalized);
  if (unwrapped && unwrapped !== normalized) {
    return NORMALIZED_KEYS[unwrapped.toLowerCase()] || null;
  }

  return null;
}
